import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { EDeliveryFilterStep } from '@/components/route/search/FilterPills/FilterPillsContentContainer';
import { EFilterCategories, ESearchFilters } from '@/constants/searchFilters';
import { ISearchFormFilters } from '@/redux/modules/searchForm';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { EDeliveryOption } from '@/services/types/core/delivery.types';

interface IBringItToMeFilterItem {
  variation: EFilterCategories;
  active: boolean;
  count: number;
  onClick: () => void;
  selected: boolean;
  title: string;
}

export const useBringItToMeFilter = (
  onClick: () => void,
  selected: boolean,
): IBringItToMeFilterItem => {
  const intl = useIntl();
  const queryParams = useSelector(getQueryParams);

  const isActive =
    queryParams?.delivery === 'true' && queryParams?.deliveryStationary === EDeliveryOption.MOVING;

  return {
    variation: EFilterCategories.DELIVERY,
    active: isActive,
    count: 0,
    onClick,
    selected,
    title: intl.formatMessage({
      defaultMessage: 'Bring it to me',
      id: 'kuVIQh',
      description: 'Filter menu: Bring it to me option for rental mode',
    }),
  };
};

export const useBringItToMeFilterState = (
  isModalOpen: boolean,
  shouldShowBringItToMeFilter: boolean,
  deliveryFilterStep: EDeliveryFilterStep,
  isDeliveryFromStore?: boolean,
  deliveryAddressFromStore?: string,
) => {
  const [disablePreviewButton, setDisablePreviewButton] = useState(
    isDeliveryFromStore && !deliveryAddressFromStore,
  );
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [originalSearchValues, setOriginalSearchValues] = useState<
    Partial<ISearchFormFilters> | undefined
  >(undefined);

  const queryParams = useSelector(getQueryParams);

  const storeOriginalSearchValues = useCallback(() => {
    const isCurrentlyDelivery = queryParams[ESearchFilters.DELIVERY] === 'true';
    if (!hasUserInteracted && !isCurrentlyDelivery) {
      const currentAddress = Array.isArray(queryParams[ESearchFilters.ADDRESS])
        ? queryParams[ESearchFilters.ADDRESS][0]
        : queryParams[ESearchFilters.ADDRESS];
      const currentMapboxId = Array.isArray(queryParams[ESearchFilters.MAPBOX_ID])
        ? queryParams[ESearchFilters.MAPBOX_ID][0]
        : queryParams[ESearchFilters.MAPBOX_ID];
      const currentFullMapboxAddress = Array.isArray(
        queryParams[ESearchFilters.FULL_MAPBOX_ADDRESS],
      )
        ? queryParams[ESearchFilters.FULL_MAPBOX_ADDRESS][0]
        : queryParams[ESearchFilters.FULL_MAPBOX_ADDRESS];

      const originalValues = {
        [ESearchFilters.ADDRESS]: currentAddress,
        [ESearchFilters.MAPBOX_ID]: currentMapboxId,
        [ESearchFilters.FULL_MAPBOX_ADDRESS]: currentFullMapboxAddress,
      };

      setOriginalSearchValues(originalValues);
    }
  }, [hasUserInteracted, queryParams]);

  useEffect(() => {
    if (deliveryFilterStep === EDeliveryFilterStep.PICKUP && disablePreviewButton) {
      setDisablePreviewButton(false);
    }
  }, [deliveryFilterStep, disablePreviewButton]);

  useEffect(() => {
    if (deliveryAddressFromStore && !hasUserInteracted) {
      setHasUserInteracted(true);
    }
  }, [deliveryAddressFromStore, hasUserInteracted]);

  // Reset interaction tracking when modal opens
  useEffect(() => {
    if (isModalOpen && shouldShowBringItToMeFilter && !deliveryAddressFromStore) {
      setHasUserInteracted(false);
    }
  }, [isModalOpen, shouldShowBringItToMeFilter, deliveryAddressFromStore]);

  const shouldDisableBringItToMeButtons =
    shouldShowBringItToMeFilter && !hasUserInteracted && !deliveryAddressFromStore;

  const handleUserInteraction = useCallback(() => {
    setHasUserInteracted(true);
  }, []);

  return {
    shouldDisableBringItToMeButtons,
    handleUserInteraction,
    originalSearchValues,
    storeOriginalSearchValues,
  };
};
